/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Rendra Pramulia
*/

import React, { useState, useEffect, useMemo } from 'react';
import { View, ScrollView, Dimensions, StyleSheet } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useHeaderHeight } from '@react-navigation/elements';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';

import * as Layout from '@components/screen/Layout';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import CustomerInfo from '@components/customer/register/CustomerInfo';
import CustomerSales from '@components/customer/register/CustomerSales';
import CustomerAddress from '@components/customer/register/CustomerAddress';
import ModalRegistration from '@components/customer/register/ModalRegistration';
import QuickRegistration from '@components/customer/register/QuickRegistration';
import { SociollaForm } from '@components/sociolla/form';
import ProgressBar from '@components/global/ProgressBar';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';

import Colors from '@global/colors';
import { showPopup } from '@/store/modal';
import { isWeb, isNative, isMobileWeb } from '@global/screens';
import { logEventScreen } from '@global/analytics';
import {
  createCustomers,
  updateCustomers,
  getCustomerId,
  getClusterByCustomerId,
  createCustomersQuick,
} from '@api/ms-account';
import * as Resources from '@/store/resources/actions';
import store from '@/store';
import { showAlert } from '@/store/global';
import {
  setDetail,
  setInvoiceAddress,
  setDeliveryAddress,
  setStoreAddress,
  setNpwpAddress,
  setKtpAddress,
  reset,
} from '@/store/customer';
import { buttonFloating } from '@assets/styles/buttonStyles';
import { convertDecimal, isVn, defaultCountry } from '@/global/helpers';
import { PAYMENT_TYPES, CUSTOMER_TYPES } from '@/constants';

const nativeFormStyles = StyleSheet.create({
  scrollView: { flex: 1 },
  // paddingBottom clears the absolutely-positioned floating Previous/Submit
  // buttons rendered below this scroll view, so the last field isn't hidden under them.
  scrollViewContent: { padding: 20, paddingBottom: 160 },
});

const validationInfo = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required('Name is required')
    .test(
      'alphabets',
      'Name must contain alphabets',
      (value) => /[^0-9]/.test(value) && /[a-zA-Z]/.test(value),
    )
    .label('Name'),
  image: Yup.array()
    .of(Yup.string().url('Invalid image URL'))
    .required('Image is required')
    .min(1, 'At least one image is required'),
  street: Yup.string().required('Street is required').label('Street'),
  street2: Yup.string().label('Street 2'),
  state: Yup.object().required('State is required').label('State'),
  city: Yup.mixed().required('City is required').label('City'),
  district: isVn()
    ? Yup.mixed().label('District')
    : Yup.mixed().required('District is required').label('District'),
  country: Yup.object().label('Country'),
  postal_code: Yup.number()
    .label('ZIP Code')
    .nullable()
    .notRequired()
    .typeError('ZIP code should number only')
    .test('maxDigits', `ZIP code must be a 5 digit number`, (value) => {
      if (value === null || value === undefined) {
        return true;
      }
      if (isVn()) {
        return true;
      }
      return String(value).length === 5;
    }),
  is_pkp: Yup.mixed()
    .required('Taxable Entrepreneur is required')
    .label('Taxable Entrepreneur'),
  wht: Yup.string()
    .required('WHT (Corporate/Personal) is required')
    .label('WHT (Corporate/Personal)'),
  phone: Yup.string()
    .matches(new RegExp('[0-9]{5}'), 'Must be a valid phone')
    .label('Phone'),
  mobile: Yup.string()
    .matches(new RegExp('[0-9]{8}'), 'Must be a valid mobile')
    .label('Phone'),
  email: Yup.string().email('Must be a valid email'),
  lat_long: Yup.mixed().label('Location'),
  owner_name: Yup.string().label('Person in Charge (PIC) Name'),
  job_position: Yup.string().label('Job Title'),
  payment_type: Yup.mixed().label('Payment Type'),
  notes: Yup.string().label('Notes'),
});

const validationSales = Yup.object().shape({
  sales_team: Yup.object()
    .required('Sales Team is required')
    .label('Sales Team'),
  market_type: Yup.object()
    .required('Market Type is required')
    .label('Market Type'),
  bussiness_type: Yup.mixed()
    .required('Business Type is required')
    .label('Business Type'),
  account: Yup.object()
    .required('Account Group is required')
    .label('Account Group'),
  user: Yup.object().required('Sales Person is required').label('Sales Person'),
  sales_admin: Yup.array()
    .min(1, 'Sales Admin is required')
    .required('Sales Admin is required')
    .label('Sales Admin'),
  special_expired_period: Yup.number()
    .required('Special Expired Date is required')
    .typeError('Special Expired Date should number only')
    .test(
      'maxValue',
      'Max Special Expired Date is 1000',
      (number) => Number(number) <= 1000,
    )
    .label('Special Expired Date'),
  payment_terms: Yup.mixed()
    .required('Payment Termns is required')
    .label('Payment Termns'),
  limit_amount: Yup.number()
    .required('Limit Amount is required')
    .typeError('Limit Amount should number only')
    .label('Limit Amount'),
  cluster: Yup.object()
    .required('Discount Regular is required')
    .test('not-empty', 'Discount Regular is required', (value) => {
      return !!value && Object.keys(value).length > 0;
    })
    .label('Cluster'),
  averageTotalPurchaseAmount: Yup.string(),
  special_discount: Yup.number()
    .typeError('Special Discount Date should number only')
    .label('Special Discount'),
  bank_name: Yup.string()
    .label('Bank Name')
    .when('market_type', (market_type) => {
      if (market_type?.code == 'GT') {
        return Yup.string()
          .required('Bank Name is required')
          .label('Bank Name');
      } else {
        return Yup.string().label('Bank Name');
      }
    }),
  bank_branch: Yup.string()
    .label('Branch')
    .when('market_type', (market_type) => {
      if (market_type?.code == 'GT') {
        return Yup.string().required('Branch is required').label('Branch');
      } else {
        return Yup.string().label('Branch');
      }
    }),
  bank_account_number: Yup.string()
    .label('Account Number')
    .when('market_type', (market_type) => {
      if (market_type?.code == 'GT') {
        return Yup.string()
          .required('Account Number is required')
          .label('Account Number');
      } else {
        return Yup.string().label('Account Number');
      }
    }),
  bank_account_name: Yup.string()
    .label('Owner')
    .when('market_type', (market_type) => {
      if (market_type?.code == 'GT') {
        return Yup.string().required('Owner is required').label('Owner');
      } else {
        return Yup.string().label('Owner');
      }
    }),
  customer_type: Yup.object().label('Customer Type'),
});

const quickRegis = {
  validation: Yup.object().shape({
    name: Yup.string()
      .required('Customer Name is required')
      .label('Customer Name'),
    contact_person: Yup.string()
      .required('Contact Person is required')
      .label('Contact Person'),
    image: Yup.array()
      .of(Yup.string().url('Invalid image URL'))
      .required('Image is required')
      .min(1, 'At least one image is required'),
    street: Yup.string().required('Street is required').label('Street'),
    state: Yup.object().required('State is required').label('State'),
    city: Yup.mixed().required('City is required').label('City'),
    district: isVn()
      ? Yup.mixed().label('District')
      : Yup.mixed().required('District is required').label('District'),
    country: Yup.object().label('Country'),
    postal_code: Yup.number()
      .typeError(`ZIP code must be a ${isVn() ? 6 : 5} digit number`)
      .test('maxDigits', `ZIP code must be a 5 digit number`, (value) => {
        if (value === null || value === undefined) {
          return true;
        }
        if (isVn()) {
          return true;
        }
        return String(value).length === 5;
      })
      .label('ZIP Code'),
    mobile: Yup.string()
      .required('Mobile is required')
      .matches(new RegExp('[0-9]{8}'), 'Must be a valid mobile')
      .label('Phone'),
    lat_long: Yup.mixed().label('Location'),
  }),
  initial: {
    name: '',
    contact_person: '',
    image: [],
    street: '',
    state: '',
    city: '',
    district: '',
    country: '',
    postal_code: '',
    mobile: '',
    lat_long: '',
  },
};

const FORM_VALUES = {
  name: '',
  image: [],
  street: '',
  street2: '',
  state: '',
  city: '',
  district: '',
  country: '',
  postal_code: '',
  is_pkp: false,
  phone: '',
  mobile: '',
  email: '',
  lat_long: '',
  owner_name: '',
  job_position: '',
  payment_type: '',
  wht: '',
  notes: '',
  sales_team: '',
  market_type: '',
  bussiness_type: '',
  account: '',
  user: '',
  beauty_advisor: [],
  merchandiser: [],
  key_account_manager: [],
  account_manager: [],
  sales_admin: [],
  regional_distributor_manager: [],
  special_expired_period: '',
  payment_terms: '',
  limit_amount: '',
  cluster: {},
  special_discount: '0',
  bank_name: '',
  bank_branch: '',
  bank_account_number: '',
  bank_account_name: '',
  customer_type: { label: '', value: '' },
};

const STEP = [
  {
    name: 'Customer Registration',
    step: 1,
    component: CustomerInfo,
    validation: validationInfo,
    formValue: FORM_VALUES,
    tempValue: {},
    next: CustomerSales,
    previous: false,
  },
  {
    name: 'Sales Info',
    step: 2,
    component: CustomerSales,
    validation: validationSales,
    formValue: FORM_VALUES,
    tempValue: {},
    next: CustomerAddress,
    previous: CustomerInfo,
  },
  {
    name: 'Customer Address',
    step: 3,
    component: CustomerAddress,
    formValue: {},
    tempValue: {},
    next: false,
    previous: CustomerSales,
  },
];

const dimension = Dimensions.get('screen');

let form = null;
let formQuick = null;
let steps = 1;

function CustomerRegistrationScreen(props) {
  const dispatch = useDispatch();
  const headerHeight = useHeaderHeight();
  const countries = useSelector((state) => state.resources.countries);
  const customer = useSelector((state) => state.customer.detail);
  const auth = useSelector((state) => state.auth);

  const [stateList, setStateList] = useState(STEP);
  const [activeStep, setActiveStep] = useState(1);
  const state = useMemo(
    () => stateList[activeStep - 1],
    [activeStep, stateList],
  );
  const [loading, setLoading] = useState(false);
  const [blur, setBlur] = useState(false);
  const [submited, setSubmited] = useState(false);
  const [isNext, setIsNext] = useState(false);
  const [isQuick, setIsQuick] = useState(false);
  const [isShowModal, setIsShowModal] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [isSubmitDraft, setIsSubmitDraft] = useState(false);

  const saveDraft = async () => {
    form.setErrors({});
    form.setTouched({});
    await generatePayload(form.values, steps, 'save-draft');
    apiCustomer('draft');
  };

  const validatePayload = (payload) => {
    if (!payload.state?.cerebro_id) {
      throw new Error('State is required');
    }
    if (!payload.sales_team?.cerebro_id) {
      throw new Error('Sales Team is required');
    }
    if (!payload.market_type?.cerebro_id) {
      throw new Error('Market Type is required');
    }
    if (!payload.market_type?.business_type?.cerebro_id) {
      throw new Error('Business Type is required');
    }

    if (!isVn()) {
      if (
        !payload.ktp_address?.identification_number ||
        !payload.ktp_address?.name ||
        !payload.ktp_address?.street ||
        !payload.ktp_address?.postal_code ||
        !payload.ktp_address?.country ||
        !payload.ktp_address?.state?.id ||
        !payload.ktp_address?.state?.cerebro_id ||
        !payload.ktp_address?.city?.id
      ) {
        throw new Error('KTP Address is incomplete');
      }

      if (
        !payload.vat_address?.identification_number ||
        !payload.vat_address?.name ||
        !payload.vat_address?.street ||
        !payload.vat_address?.postal_code ||
        !payload.vat_address?.country ||
        !payload.vat_address?.state?.id ||
        !payload.vat_address?.state?.cerebro_id ||
        !payload.vat_address?.city?.id
      ) {
        throw new Error('VAT Address is incomplete');
      }
    }

    if (payload.delivery_address.length <= 0) {
      throw new Error('Delivery Address cannot be empty');
    }
    if (payload.invoice_address.length <= 0) {
      throw new Error('Invoice Address cannot be empty');
    }

    const deliveryNames = new Set();
    for (let i = 0; i < payload.delivery_address.length; i++) {
      const delAdd = payload.delivery_address[i];
      if (
        !delAdd?.name ||
        !delAdd?.contact_person ||
        !delAdd?.state?.id ||
        !delAdd?.state?.cerebro_id ||
        !delAdd?.city?.id ||
        !delAdd?.street ||
        !delAdd?.mobile ||
        (!isVn() && !delAdd?.postal_code)
      ) {
        console.log('Delivery Address Data: ', delAdd);
        throw new Error('Delivery Address is incomplete');
      }

      const normalizedName = String(delAdd.name).trim().toLowerCase();
      if (deliveryNames.has(normalizedName)) {
        throw new Error('Delivery Address name must be unique');
      }
      deliveryNames.add(normalizedName);
    }

    for (let i = 0; i < payload.invoice_address.length; i++) {
      const invAdd = payload.invoice_address[i];
      if (
        !invAdd?.name ||
        !invAdd?.state?.id ||
        !invAdd?.state?.cerebro_id ||
        !invAdd?.city?.id ||
        !invAdd?.street ||
        !invAdd?.mobile ||
        (!isVn() && !invAdd?.postal_code)
      ) {
        console.log('Invoice Address Data: ', invAdd);
        throw new Error('Invoice Address is incomplete');
      }
    }
  };

  const validatePayloadDraft = (payload) => {
    const showError = (msg) => {
      Toast.show({
        text1: msg,
        type: 'error',
      });
    };

    // Upload Photo
    if (
      !payload.image ||
      !Array.isArray(payload.image) ||
      payload.image.length === 0
    ) {
      showError('Upload Photo is required');
      return false;
    }

    // Name
    if (!payload.name || !String(payload.name).trim()) {
      showError('Name is required');
      return false;
    }

    // Address
    if (!payload.street || !String(payload.street).trim()) {
      showError('Street is required');
      return false;
    }
    if (!payload.state || !(payload.state.id || payload.state.cerebro_id)) {
      showError('State is required');
      return false;
    }
    if (!payload.city || !payload.city.id) {
      showError('City is required');
      return false;
    }
    if (!isVn() && (!payload.district || !payload.district.id)) {
      showError('District is required');
      return false;
    }

    // ZIP (except VN)
    if (!isVn()) {
      if (
        payload.postal_code === null ||
        payload.postal_code === undefined ||
        String(payload.postal_code).trim() === ''
      ) {
        showError('ZIP Code is required');
        return false;
      }
      const zip = String(payload.postal_code);
      if (!/^\d{5}$/.test(zip)) {
        showError('ZIP code must be a 5 digit number');
        return false;
      }
    }

    // Taxable & WHT
    if (
      payload.is_pkp === undefined ||
      payload.is_pkp === null ||
      payload.is_pkp === ''
    ) {
      showError('Taxable Entrepreneur is required');
      return false;
    }
    if (!payload.wht || String(payload.wht).trim() === '') {
      showError('WHT (Corporate/Personal) is required');
      return false;
    }

    const deliveryAddresses = payload.delivery_address || [];
    const deliveryNames = new Set();
    for (let i = 0; i < deliveryAddresses.length; i++) {
      const name = deliveryAddresses[i]?.name;
      if (!name) {
        continue;
      }

      const normalizedName = String(name).trim().toLowerCase();
      if (!normalizedName) {
        continue;
      }

      if (deliveryNames.has(normalizedName)) {
        showError('Delivery Address name must be unique');
        return false;
      }

      deliveryNames.add(normalizedName);
    }

    return true; // valid
  };

  const generatePayload = (data, step, status = '') => {
    const progress = step ? step : activeStep;
    const cus = JSON.parse(JSON.stringify(store.getState().customer.detail));
    const payload = { ...data };

    let formFormik = {};
    if (form?.values) {
      formFormik = form.values;
    }
    // Step 1
    const cn = countries.find((x) => x.name.toLowerCase() === defaultCountry());
    if (data.is_pkp) {
      payload.is_pkp = data.is_pkp.value;
    }
    if (data.city) {
      payload.city = {
        id: data.city._id || data.city.id,
        name: data.city.name,
      };
      payload.country = {
        name: cn?.name || data.country?.name,
        id: cn?._id || data.country?._id,
        cerebro_id: cn?.odoo_country_id || data.country?.cerebro_id || '',
      };
    }
    if (data.district) {
      payload.district = {
        id: data.district._id || data.district.id,
        name: data.district.name,
      };
    }
    if (data.state) {
      payload.state = {
        name: data.state.name,
        id: data.state._id ? data.state._id : data.state.id,
        cerebro_id: data?.state?.odoo_state_id
          ? data.state.odoo_state_id
          : data?.state?.cerebro_id
          ? data.state.cerebro_id
          : '',
      };
    }
    payload.owner_name = payload.owner_name || formFormik?.owner_name;
    payload.job_position = payload.job_position || formFormik?.job_position;
    payload.payment_type =
      payload.payment_type?.value || formFormik?.payment_type?.value;

    // Step 2
    if (data.sales_team?.id) {
      payload.sales_team = {
        id: data.sales_team.id,
        name: data.sales_team.name,
        cerebro_id: data.sales_team.cerebro_id,
        warehouse: data.sales_team.warehouse,
      };
    }
    if (data.market_type?.id) {
      payload.market_type = {
        id: data.market_type.id,
        code: data.market_type.code,
        cerebro_id: data.market_type.cerebro_id || '',
        business_type: {
          ...data.bussiness_type,
          account: data.account,
        },
      };
    }
    if (data.user?.id) {
      payload.user = {
        id: data.user.id,
        name: data.user.name,
        branch_code: data?.sales_team?.branch_code
          ? data.sales_team.branch_code
          : data?.user?.branch_code
          ? data.user.branch_code
          : '',
        sales_code: data?.sales_team?.sales_code
          ? data.sales_team.sales_code
          : data?.user?.sales_code
          ? data.user.sales_code
          : '',
      };
    }
    if (data.beauty_advisor?.length) {
      payload.beauty_advisor = data.beauty_advisor.map((ba) => {
        return { id: ba.id, name: ba.name };
      });
    }
    if (data.merchandiser?.length) {
      payload.merchandiser = data.merchandiser.map((ma) => {
        return { id: ma.id, name: ma.name };
      });
    }
    if (data.key_account_manager?.length) {
      payload.key_account_manager = data.key_account_manager.map((kam) => {
        return { id: kam.id, name: kam.name };
      });
    }
    if (data.account_manager?.length) {
      payload.account_manager = data.account_manager.map((am) => {
        return { id: am.id, name: am.name };
      });
    }
    if (data.sales_admin?.length) {
      payload.sales_admin = data.sales_admin.map((sa) => {
        return { id: sa.id, name: sa.name };
      });
    }
    if (data.regional_distributor_manager?.length) {
      payload.regional_distributor_manager =
        data.regional_distributor_manager.map((rdm) => {
          return { id: rdm.id, name: rdm.name };
        });
    }
    payload.special_expired_period =
      data.special_expired_period || formFormik?.special_expired_period || '';
    payload.payment_terms =
      data.payment_terms || formFormik?.payment_terms || '';
    payload.limit_amount = data.limit_amount
      ? Number(data.limit_amount)
      : formFormik?.limit_amount
      ? Number(formFormik?.limit_amount)
      : '';
    payload.special_discount = data.special_discount
      ? Number(data.special_discount)
      : formFormik?.special_discount
      ? Number(formFormik.special_discount)
      : 0;
    if (data.bussiness_type?._id) {
      payload.market_type.business_type.id =
        payload.market_type.business_type._id;
      delete payload.market_type.business_type._id;
      if (cus.market_type?.business_type?._id) {
        delete cus.market_type.business_type._id;
      }
    }
    if (data.account?._id) {
      payload.market_type.business_type.account.id =
        payload.market_type.business_type.account._id;
      delete payload.market_type.business_type.account._id;
      if (cus.market_type?.business_type?.account?._id) {
        delete cus.market_type.business_type.account._id;
      }
    }
    if (data?.cluster?.id || data?.cluster?._id) {
      payload.cluster = {
        id: data.cluster?.id || data.cluster?.id,
        name: data.cluster?.name,
      };
    } else {
      payload.cluster = {};
    }
    payload.averageTotalPurchaseAmount = data.averageTotalPurchaseAmount;
    payload.bank_account = {
      name: data?.bank_name || formFormik?.bank_name,
      branch: data?.bank_branch || formFormik?.bank_branch,
      account_number:
        data?.bank_account_number || formFormik?.bank_account_number,
      owner_name: data?.bank_account_name || formFormik?.bank_account_name,
    };
    payload.customer_type = data?.customer_type || formFormik?.customer_type;

    if (payload.customer_type?.value?._id) {
      payload.customer_type = {
        _id: payload.customer_type.value._id,
        name: payload.customer_type.value.name,
        description: payload.customer_type.value.description,
      };
    } else if (payload.customer_type?._id) {
      payload.customer_type = {
        _id: payload.customer_type._id,
        name: payload.customer_type.name,
        description: payload.customer_type.description,
      };
    } else if (payload.customer_type?.value) {
      payload.customer_type = payload.customer_type.value;
    }
    if (!payload.customer_type) {
      payload.customer_type = '';
    }

    if (progress != 3) {
      dispatch(setDetail({ ...cus, ...payload }));
      if (status != 'save-draft') {
        setStateList([
          ...stateList.slice(0, progress - 1),
          {
            ...state,
            formValue: setNewValue({ ...cus, ...payload }, state.formValue),
          },
          ...stateList.slice(progress),
        ]);
      }
    }
  };

  const apiCustomer = async (status = 'need_approval') => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const customerStore = JSON.parse(
        JSON.stringify(store.getState().customer),
      );
      const cus = { ...customerStore.detail };
      const preserveLatLong = (addr) => {
        if (addr?.lat_long?.latitude && addr?.lat_long?.longitude) {
          const latitude = convertDecimal(addr.lat_long.latitude, 13);
          const longitude = convertDecimal(addr.lat_long.longitude, 13);
          addr.lat_long = {
            latitude:
              parseFloat(addr.lat_long.latitude) === latitude
                ? addr.lat_long.latitude
                : latitude,
            longitude:
              parseFloat(addr.lat_long.longitude) === longitude
                ? addr.lat_long.longitude
                : longitude,
          };
        }
        return addr;
      };
      cus['delivery_address'] = (customerStore.delivery_address || []).map(
        preserveLatLong,
      );
      cus['invoice_address'] = customerStore.invoice_address;

      if (Object.keys(customerStore.ktp_address).length > 0) {
        cus['ktp_address'] = {
          ...customerStore.ktp_address,
          identification_number: customerStore.ktp_address.ktp,
        };
      } else {
        if (status !== 'draft') {
          delete cus.ktp_address;
        } else {
          cus.ktp_address = {};
        }
      }

      if (Object.keys(customerStore.npwp_address).length > 0) {
        cus['vat_address'] = {
          ...customerStore.npwp_address,
          identification_number: customerStore.npwp_address.vat,
        };
      } else {
        if (status !== 'draft') {
          delete cus.vat_address;
        } else {
          cus.vat_address = {};
        }
      }
      cus['store_address'] = (customerStore.store_address || []).map(
        preserveLatLong,
      );

      delete cus.bussiness_type;
      delete cus.account;

      if (status == 'need_approval') {
        try {
          validatePayload(cus);
        } catch (eVal) {
          Toast.show({
            text1: eVal.message,
            type: 'error',
          });
          dispatch(showPopup({ name: 'isLoading', value: false }));
          return;
        }
      }

      if (status == 'draft') {
        const isValid = validatePayloadDraft(cus);
        if (!isValid) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          return;
        }
      }

      const customerIdToUse = props.route.params?.id || cus?._id;
      if (customerIdToUse) {
        cus['status'] = status;
        const updatedCustomer = await updateCustomers(cus._id, cus);
        const isApprovalRequired =
          updatedCustomer?.data?.data?.is_approval_required;
        if (status != 'draft' && isApprovalRequired) {
          dispatch(
            showAlert({
              show: true,
              title: 'Customer Data Change Submitted',
              description:
                'Changes in approved customer data requires an approval. You’ll be notified once the customer registration has been approved.',
              isConfirm: false,
              testID: 'customer-registration-change-submitted',
              onCallback: () => {
                props.navigation.reset({
                  index: 0,
                  routes: [
                    {
                      name: 'CustomerNavigator',
                      state: {
                        routes: [
                          {
                            name: 'CustomerDetailScreen',
                            params: {
                              id: customerIdToUse,
                            },
                          },
                        ],
                      },
                    },
                  ],
                });
              },
            }),
          );
        } else {
          setIsSubmitDraft(true);
          setSubmited(true);
          dispatch(showPopup({ name: 'isLoading', value: false }));
          if (isWeb || !isNative) {
            props.navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'CustomerNavigator',
                  state: {
                    routes: [
                      {
                        name: 'CustomerDetailScreen',
                        params: {
                          id: customerIdToUse,
                        },
                      },
                    ],
                  },
                },
              ],
            });
          } else {
            props.navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'CustomerNavigator',
                  state: {
                    routes: [
                      {
                        name: 'CustomerDetailScreen',
                        params: {
                          id: customerIdToUse,
                        },
                      },
                    ],
                  },
                },
              ],
            });
          }
        }
      } else {
        cus['status'] = status;
        await createCustomers(cus);
        if (status === 'draft') {
          setIsSubmitDraft(true);
          setSubmited(true);
          dispatch(showPopup({ name: 'isLoading', value: false }));
          if (isWeb || !isNative) {
            props.navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'CustomerNavigator',
                  state: {
                    routes: [
                      {
                        name: 'CustomerScreen',
                        params: { isHome: '', shouldRefresh: true },
                      },
                    ],
                  },
                },
              ],
            });
          } else {
            props.navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'CustomerNavigator',
                  state: {
                    routes: [
                      {
                        name: 'CustomerScreen',
                        params: { isHome: '', shouldRefresh: true },
                      },
                    ],
                  },
                },
              ],
            });
          }
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Customer Registration Submitted',
              description:
                'Customer registration submitted, you’ll be notified once the customer registration has been approved.',
              isConfirm: false,
              testID: 'customer-registration-submitted',
              onCallback: () => {
                props.navigation.reset({
                  index: 0,
                  routes: [
                    {
                      name: 'CustomerNavigator',
                      state: {
                        routes: [
                          {
                            name: 'CustomerScreen',
                            params: { isHome: '', shouldRefresh: true },
                          },
                        ],
                      },
                    },
                  ],
                });
              },
            }),
          );
        }
      }
      if (!isSubmitDraft) {
        setSubmited(true);
        dispatch(showPopup({ name: 'isLoading', value: false }));
      }
    } catch (e) {
      let message = 'A system error occurred';
      if (e?.response?.data?.message && e?.response?.data?.errorCode) {
        message = e.response.data.message;
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: message,
        type: 'error',
      });
    }
  };

  const previous = () => {
    setIsNext(false);
    setLoading(true);

    const formData = form.values;
    const storeData = store.getState().customer.detail;

    const mergedData = {
      ...storeData,
      ...formData,
    };

    const step = state.step - 1;
    const activeState = stateList[step - 1];

    const updatedFormValue = setNewValue(mergedData, activeState.formValue);

    dispatch(setDetail({ ...mergedData, ...updatedFormValue }));
    setStateList([
      ...stateList.slice(0, step - 1),
      {
        ...activeState,
        formValue: updatedFormValue,
      },
      ...stateList.slice(step),
    ]);

    steps = step;
    setActiveStep((prev) => prev - 1);
    setTimeout(() => setLoading(false), 100);
  };

  const next = async () => {
    setLoading(true);
    const cus = store.getState().customer.detail;
    // console.log(cus)
    const step = state.step + 1;
    const activeState = stateList[step - 1];
    if (step < stateList.length) {
      setStateList([
        ...stateList.slice(0, step - 1),
        {
          ...activeState,
          formValue: setNewValue(cus, activeState.formValue),
        },
        ...stateList.slice(step),
      ]);
      dispatch(
        setDetail({ ...cus, ...setNewValue(cus, activeState.formValue) }),
      );
    }
    steps = step;
    setActiveStep((prev) => prev + 1);
    setTimeout(() => setLoading(false), 100);
  };

  const setNewValue = (source, destination) => {
    const formValue = {};
    for (const key of Object.keys(destination)) {
      if (key == 'city') {
        formValue[key] = {
          _id: source[key]?._id,
          id: source[key]?.id,
          name: source[key]?.name ? source[key].name : source[key],
        };
      } else if (key == 'special_expired_period' && source[key]) {
        const value = source[key].split('_')[0];
        formValue[key] = value || '';
      } else if (key === 'bussiness_type') {
        const raw = source[key];
        const nested = source.market_type?.business_type;

        if ((raw?.id || raw?._id) && raw?.code) {
          formValue[key] = raw;
        } else if ((nested?.id || nested?._id) && nested?.code) {
          formValue[key] = nested;
        } else {
          formValue[key] = '';
        }
      } else if (key === 'account') {
        const raw = source[key];
        const nested =
          source.market_type?.business_type?.account || source?.account;

        if ((raw?.id || raw?._id) && raw?.code) {
          // if (source?.business_type?._id !== destination?.business_type?._id) {
          formValue[key] = raw;
          // } else {
          //   formValue[key] = destination[key];
          // }
        } else if ((nested?.id || nested?._id) && nested?.code) {
          formValue[key] = nested;
        } else {
          formValue[key] = '';
        }
      } else if (key === 'limit_amount') {
        formValue[key] =
          source.limit_amount || source.limit_amount == 0
            ? source.limit_amount.toString()
            : '';
      } else if (key === 'bank_account') {
        formValue['bank_name'] = source?.[key]?.name || '';
        formValue['bank_branch'] = source?.[key]?.branch || '';
        formValue['bank_account_number'] = source?.[key]?.account_number || '';
        formValue['bank_account_name'] = source?.[key]?.owner_name || '';
      } else if (key === 'customer_type') {
        const raw = source.customer_type;
        if (raw?.value?._id && raw?.label) {
          formValue.customer_type = raw;
        } else if (raw?._id) {
          formValue.customer_type = {
            value: {
              _id: raw._id,
              name: raw.name,
              description: raw.description,
            },
            label: raw.name,
          };
        } else {
          formValue.customer_type = raw
            ? CUSTOMER_TYPES.find((a) => a.value == raw)
            : { label: '', value: '' };
        }
      } else if (key === 'payment_type') {
        formValue[key] = source[key]
          ? PAYMENT_TYPES.find((a) => a.value == source[key]) || ''
          : '';
      } else if (key === 'special_discount') {
        formValue[key] = source.special_discount
          ? source.special_discount.toString()
          : '0';
      } else if (key == 'is_pkp') {
        formValue[key] = {
          name:
            typeof source[key] === 'boolean'
              ? source[key]
                ? 'Taxable'
                : 'Non Taxable'
              : source[key]?.name ?? '',
          value:
            typeof source[key] === 'boolean'
              ? source[key]
              : !!source[key]?.value,
        };
      } else {
        formValue[key] = source[key] || '';
      }
    }
    return formValue;
  };

  const handleSubmit = async (data) => {
    if (isNative && isQuick) {
      submitQuick(data);
      return;
    }
    setLoading(true);
    try {
      await generatePayload(data);
      if (isNext) {
        next();
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      Toast.show({
        text1: 'Something Wrong, please try again.',
        type: 'error',
      });
    }
  };

  const submitQuick = async (data) => {
    if (!data.lat_long) {
      Toast.show({
        text1: 'Please add location.',
        type: 'error',
      });
      return;
    }
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const payload = {
        name: data.name,
        image: data.image,
        street: data.street,
        state: {
          name: data.state?.name,
          id: data.state?._id,
          cerebro_id: data.state?.odoo_state_id,
        },
        city: {
          id: data.city?._id,
          name: data.city?.name,
        },
        district: {
          id: data.district?._id,
          name: data.district?.name,
        },
        postal_code: data.postal_code,
        mobile: data.mobile,
        contact_person: data.contact_person,
        lat_long: data.lat_long,
      };
      await createCustomersQuick(payload);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      dispatch(
        showAlert({
          show: true,
          title: 'Customer Registration Submitted',
          description:
            'Customer has been saved in the draft state successfully',
          isConfirm: false,
          testID: 'customer-registration-draft-submitted',
          onCallback: () => {
            props.navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'CustomerNavigator',
                  state: {
                    routes: [
                      {
                        name: 'CustomerScreen',
                        params: { isHome: '', shouldRefresh: true },
                      },
                    ],
                  },
                },
              ],
            });
          },
        }),
      );
    } catch (e) {
      let message = 'A system error occurred';
      if (e?.response?.data?.message && e?.response?.data?.errorCode) {
        message = e.response.data.message;
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: message,
        type: 'error',
      });
    }
  };

  const submit = async () => {
    if (isQuick) {
      return;
    }
    const del = store.getState().customer.delivery_address;
    const inv = store.getState().customer.invoice_address;
    if (del.length && inv.length) {
      await generatePayload(form.values);
      apiCustomer();
    } else {
      Toast.show({
        text1:
          'Please input at least 1 Delivery Address and 1 Invoice Address.',
        type: 'error',
        props: {
          text1NumberOfLines: 2,
        },
      });
    }
  };

  const CustomerForm = (propsForm) => {
    const Form = propsForm.state.component;
    return <Form {...propsForm} />;
  };

  const callback = (data) => {
    if (data.key === 'draft') {
      saveDraft();
    }
  };

  const confirmLeave = (route, type = null, cancel = false) => {
    const others = cancel ? {} : { others: 'SAVE DRAFT' };
    dispatch(
      showAlert({
        show: true,
        title: 'Discard',
        description: 'Unsaved data will be deleted. Do you want to proceed?',
        ...others,
        testID: 'customer-registration-discard',
        onCallback: (val) => {
          if (val) {
            if (!cancel) {
              saveDraft();
            }
          } else {
            if (type === 'dispatch') {
              props.navigation.dispatch(route);
            } else {
              props.navigation.navigate(route);
            }
          }
        },
      }),
    );
  };

  const getClusterCustomer = async (data) => {
    if (
      data.status &&
      data.status === 'approved' &&
      data._id &&
      Boolean(
        ['superadmin', 'sales_admin', 'general'].indexOf(
          auth.user?.role?.code || '',
        ) > -1,
      )
    ) {
      const response = await getClusterByCustomerId(data._id);
      const cluster = response.data.data?.cluster || {};
      const cus = store.getState().customer.detail;
      dispatch(
        setDetail({
          ...cus,
          cluster,
          averageTotalPurchaseAmount:
            response.data.data?.average_purchase_amount || 0,
        }),
      );
    }
  };

  useEffect(() => {
    if (form && submited === false) {
      props.navigation.setOptions({
        title: state.name,
        headerLeft: () => (
          <Layout.BackCustom
            onCallback={() =>
              state.step > 1 ? previous() : confirmLeave('CustomerNavigator')
            }
          />
        ),
      });
    } else {
      props.navigation.setOptions({
        title: state.name,
        headerLeft: () => (
          <Layout.BackCustom
            onCallback={() =>
              state.step > 1
                ? previous()
                : props.navigation.navigate('CustomerNavigator')
            }
          />
        ),
      });
    }
  }, [submited]);

  const onLeave = (e) => {
    dispatch(reset());
    // if (form && !submited) {
    //   e.preventDefault();
    //   confirmLeave(e.data.action, 'dispatch');
    // }
  };

  const getAccountBankData = (bankAccount) => {
    return {
      bank_account_name: bankAccount?.owner_name || '',
      bank_account_number: bankAccount?.account_number || '',
      bank_branch: bankAccount?.branch || '',
      bank_name: bankAccount?.name || '',
    };
  };

  const formatData = (data) => {
    const accountBankData = getAccountBankData(data?.bank_account || {});
    const newData = { ...data, ...accountBankData };
    if (Object.keys(data).length) {
      delete newData.approval_roles;
      delete newData.approvals;
      delete newData.created_at;
      delete newData.created_by;
      delete newData.credit_limit_status;
      delete newData.documents;
      delete newData.is_deleted;
      delete newData.is_limit_update;
      delete newData.loyalty_points;
      delete newData.name_lower;
      delete newData.state_histories;
      delete newData.updated_at;
    }
    if (newData.state?.id) {
      newData.state._id = newData.state.id;
    }
    if (newData.city?.id) {
      newData.city._id = newData.city.id;
    }
    return newData;
  };

  const onFocus = () => {
    setBlur(false);
    setLoading(true);
    dispatch(Resources.getCountries({}));
    let customerId = props?.route?.params?.id;
    if (!customerId) {
      try {
        const routes = props.navigation.getState()?.routes;
        if (routes?.length && routes[routes.length - 1]) {
          const current_route = routes[routes.length - 1];
          if (
            current_route?.params?.id &&
            current_route?.name == 'CustomerRegistrationScreen'
          ) {
            customerId = current_route.params.id;
          }
        }
      } catch (error) {}
    }
    if (customerId) {
      try {
        getCustomerId(customerId).then((res) => {
          const formattedData = formatData(res.data.data);
          getClusterCustomer(res.data.data);
          dispatch(setDetail(formattedData));
          dispatch(setInvoiceAddress(res.data.data?.invoice_address || []));
          dispatch(setDeliveryAddress(res.data.data?.delivery_address || []));
          dispatch(setStoreAddress(res.data.data?.store_address || []));
          dispatch(setKtpAddress(res.data.data?.ktp_address || {}));
          dispatch(setNpwpAddress(res.data.data?.vat_address || {}));
          if (activeStep !== 3) {
            setStateList([
              ...stateList.slice(0, activeStep - 1),
              {
                ...state,
                formValue: setNewValue(formattedData, state.formValue),
              },
              ...stateList.slice(activeStep),
            ]);
          }
          setLoading(false);
        });
      } catch (e) {
        setLoading(false);
      }
    } else {
      if (isNative) {
        if (!isShowModal) {
          setTimeout(() => {
            setIsShowModal(true);
          }, 250);
          props.navigation.setOptions({
            headerRight: () => (
              <MoreButton
                menu={[{ type: 'props', name: 'Save Draft', key: 'draft' }]}
                callback={(value) => callback(value)}
              />
            ),
          });
        }
      } else {
        if (isShowModal) {
          setIsShowModal(false);
        }
        setIsQuick(false);
        setTimeout(() => setLoading(false), 500);
      }
    }
  };

  const FORM = useMemo(() => {
    if (blur) {
      return false;
    }
    return (
      <CustomerForm
        state={state}
        data={customer}
        onSubmit={(val) => handleSubmit(val)}
      />
    );
  }, [customer, state, blur]);

  useEffect(() => {
    if (form?.values?.name || !props.route?.params?.id) {
      setBlur(false);
      form = null;
      steps = 1;
      dispatch(reset());
      setActiveStep(1);
      setStateList(STEP);
      setTimeout(() => {
        onFocus();
      }, 100);
    }
  }, [props]);

  useEffect(() => {
    props.navigation.addListener('blur', () => {
      setBlur(true);
      form = null;
      steps = 1;
      formQuick = null;
      dispatch(reset());
      setActiveStep(1);
      setIsShowModal(false);
      setIsQuick(false);
      setFormReady(false);
    });

    props.navigation.addListener('focus', onFocus);

    props.navigation.addListener('beforeRemove', onLeave);

    if (state && props.navigation) {
      props.navigation.setOptions({
        title: state.name,
        headerLeft: () => (
          <Layout.BackCustom
            onCallback={() =>
              state.step > 1
                ? previous()
                : props.navigation.navigate('CustomerNavigator', {
                    screen: 'CustomerScreen',
                    params: {
                      isHome: '',
                    },
                  })
            }
          />
        ),
      });
    }

    if (!state && props.navigation) {
      props.navigation.setOptions({
        title: props.route.params.name,
        headerLeft: () => (
          <Layout.BackCustom
            onCallback={() =>
              props.navigation.replace('CustomerNavigator', {
                screen: 'CustomerScreen',
                params: {
                  isHome: '',
                },
              })
            }
          />
        ),
      });
    }

    return function cleanup() {
      props.navigation.removeListener('beforeRemove', onLeave);
      props.navigation.removeListener('focus', onFocus);
      props.navigation.removeListener('blur');
    };
  }, [props.navigation, form, state]);

  useEffect(() => {
    if (isQuick) {
      props.navigation.setOptions({
        headerRight: () => {},
        title: 'Quick Customer Registration',
        headerLeft: () => (
          <Layout.BackCustom
            onCallback={() => confirmLeave('CustomerNavigator', null, true)}
          />
        ),
      });
    } else {
      props.navigation.setOptions({
        headerRight: () => {
          return isWeb ? (
            <NotificationSettingButton {...props} />
          ) : (
            <MoreButton
              menu={[{ type: 'props', name: 'Save Draft', key: 'draft' }]}
              callback={(value) => callback(value)}
            />
          );
        },
      });
    }
  }, [isQuick]);

  return (
    <>
      <Layout.Container testID="customer-registration-screen">
        {isWeb ? (
          <>
            <Layout.Wrapper white={false}>
              <Layout.Title
                title={
                  props?.route?.params?.id
                    ? `${customer?.name || 'Edit Customer'}`
                    : 'Customer Registration'
                }>
                <View style={{ flexDirection: 'row' }}>
                  <Button
                    testID="customer-registration-save-draft-btn"
                    type="primary"
                    rounded={true}
                    buttonStyle={{ width: 160 }}
                    title={
                      <View
                        style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Image
                          style={{ width: 14, height: 14 }}
                          width={14}
                          height={14}
                          svg={true}
                          source={require('@assets/img/icons/edit-square-white.svg')}
                        />
                        <SociollaText
                          type="medium"
                          style={{
                            fontSize: 14,
                            color: Colors.white,
                            marginLeft: 12,
                          }}>
                          Save Draft
                        </SociollaText>
                      </View>
                    }
                    onPress={saveDraft}
                  />
                  <Button
                    testID="customer-registration-previous-btn"
                    type="primary"
                    rounded={true}
                    buttonStyle={{ width: 120, marginLeft: 15 }}
                    title={
                      <SociollaText
                        type="medium"
                        style={{ fontSize: 14, color: Colors.white }}>
                        Previous
                      </SociollaText>
                    }
                    disabled={!state.previous}
                    disabledStyle={{ width: 120 }}
                    onPress={previous}
                  />
                  {state.step === 3 ? (
                    <Button
                      testID="customer-registration-submit-btn"
                      type="primary"
                      rounded={true}
                      buttonStyle={{
                        width: 120,
                        marginLeft: 15,
                        backgroundColor: Colors.blue0,
                      }}
                      title={
                        <SociollaText
                          type="medium"
                          style={{ fontSize: 14, color: Colors.white }}>
                          Submit
                        </SociollaText>
                      }
                      disabledStyle={{ width: 120 }}
                      onPress={submit}
                    />
                  ) : (
                    <Button
                      testID="customer-registration-next-btn"
                      type="primary"
                      rounded={true}
                      buttonStyle={{ width: 120, marginLeft: 15 }}
                      title={
                        <SociollaText
                          type="medium"
                          style={{ fontSize: 14, color: Colors.white }}>
                          Next
                        </SociollaText>
                      }
                      disabled={!state.next}
                      disabledStyle={{ width: 120 }}
                      onPress={() => {
                        setIsNext(true);
                        form.handleSubmit();
                      }}
                    />
                  )}
                </View>
              </Layout.Title>
            </Layout.Wrapper>
            <Layout.Wrapper fullWidth={true}>
              <ScrollView
                contentContainerStyle={{ padding: 20, paddingBottom: 40 }}>
                {loading ? (
                  <Layout.Loader />
                ) : (
                  <View>
                    <View
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: 20,
                      }}>
                      <View style={{ flex: 1, overflow: 'hidden' }}>
                        <ProgressBar
                          testID="customer-registration-progress-bar"
                          key="progress-bar"
                          page={activeStep}
                          totalPage={stateList.length}
                        />
                      </View>
                      <View
                        style={{
                          marginLeft: 12,
                        }}>
                        <SociollaText
                          style={{ fontSize: 12, color: Colors.black2 }}>
                          {'Step '}
                          {activeStep}
                          {' of '}
                          {stateList.length}
                        </SociollaText>
                      </View>
                    </View>
                    <SociollaForm
                      initialValues={state.formValue}
                      onSubmit={handleSubmit}
                      validationSchema={state.validation}
                      innerRef={(formik) => (form = formik)}>
                      {FORM}
                    </SociollaForm>
                  </View>
                )}
              </ScrollView>
            </Layout.Wrapper>
          </>
        ) : (
          <View
            style={
              isMobileWeb
                ? { height: `calc(100dvh - ${headerHeight}px)` }
                : { flex: 1 }
            }>
            {!isQuick && (
              <ProgressBar
                testID="customer-registration-progress-bar"
                key="progress-bar"
                page={activeStep}
                totalPage={stateList.length}
              />
            )}
            <KeyboardAwareScrollView
              style={nativeFormStyles.scrollView}
              showsVerticalScrollIndicator={Boolean(isWeb)}
              contentContainerStyle={nativeFormStyles.scrollViewContent}
              enableOnAndroid={true}
              extraScrollHeight={160}>
              {loading ? (
                <Layout.Loader />
              ) : isQuick && isNative ? (
                <SociollaForm
                  initialValues={quickRegis.initial}
                  onSubmit={handleSubmit}
                  validationSchema={quickRegis.validation}
                  innerRef={(formik) => (formQuick = formik)}>
                  <QuickRegistration />
                </SociollaForm>
              ) : (
                <SociollaForm
                  initialValues={state.formValue}
                  onSubmit={handleSubmit}
                  validationSchema={state.validation}
                  innerRef={(formik) => (form = formik)}>
                  <CustomerForm
                    state={state}
                    data={customer}
                    onSubmit={(val) => handleSubmit(val)}
                  />
                </SociollaForm>
              )}
            </KeyboardAwareScrollView>
          </View>
        )}
        {!isWeb && !loading && (
          <View
            style={[
              buttonFloating,
              { width: dimension.width, position: 'absolute' },
            ]}>
            {state.step === 3 || (isNative && isQuick && formReady) ? (
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                {!isQuick && (
                  <Button
                    testID="customer-registration-previous-btn-mobile"
                    type="primary"
                    rounded
                    buttonStyle={{ width: '100%', marginBottom: 10 }}
                    disabledStyle={{ width: '100%' }}
                    disabled={!state.previous}
                    title={
                      <SociollaText
                        type="medium"
                        style={{ fontSize: 14, color: Colors.white }}>
                        Previous
                      </SociollaText>
                    }
                    onPress={previous}
                  />
                )}
                <Button
                  testID="customer-registration-submit-btn-mobile"
                  rounded
                  buttonStyle={{ width: '100%' }}
                  disabledStyle={{ width: '100%' }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      Submit
                    </SociollaText>
                  }
                  onPress={() =>
                    isNative && isQuick ? formQuick.handleSubmit() : submit()
                  }
                />
              </View>
            ) : (
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                <Button
                  testID="customer-registration-previous-btn-mobile"
                  type="primary"
                  rounded
                  buttonStyle={{ width: '100%', marginBottom: 10 }}
                  disabledStyle={{ width: '100%' }}
                  disabled={!state.previous}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      Previous
                    </SociollaText>
                  }
                  onPress={previous}
                />
                <Button
                  testID="customer-registration-next-btn-mobile"
                  rounded
                  buttonStyle={{ width: '100%' }}
                  disabledStyle={{ width: '100%' }}
                  disabled={!state.next}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      Next
                    </SociollaText>
                  }
                  onPress={() => {
                    setIsNext(true);
                    form.handleSubmit();
                  }}
                />
              </View>
            )}
          </View>
        )}
      </Layout.Container>
      {isNative ? (
        <ModalRegistration
          testID="customer-registration-modal"
          show={isShowModal}
          onClose={() => {
            props.navigation.navigate('CustomerNavigator', {
              screen: 'CustomerScreen',
              params: {
                isHome: '',
              },
            });
          }}
          onSubmit={(val) => {
            const isQuickRegister = Boolean(val === 'quick');
            setIsShowModal(false);
            setIsQuick(isQuickRegister);
            setLoading(false);
            setTimeout(() => setFormReady(true), 500);
            if (isQuickRegister) {
              logEventScreen('QuickCustomerRegistrationScreen');
            }
          }}
        />
      ) : (
        false
      )}
    </>
  );
}

export default CustomerRegistrationScreen;
